import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import MedPurchaseEditModal from "./MedPurchaseEditModal";

// ✅ COMPACTED DYNAMIC STYLES FOR REACT SELECT
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 1px var(--theme-color, #0088cc)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    borderWidth: '1px',
    backgroundColor: 'white'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: '30px' }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '4px', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e0e6ed' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const MedicinePurchaseSearch = () => {
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    vendorId: null, vendorName: ""
  });

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPurchase, setEditPurchase] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const loadVendorOptions = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return [];
   try {
      const res = await customFetch(`/VendorDT/search?name=${encodeURIComponent(inputValue.trim())}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((v) => ({ value: v.vid, label: v.vendorName }));
    } catch (err) { console.error(err); return []; }
  };

  const handleVendorChange = (selected) => {
    setFilters(prev => ({ ...prev, vendorId: selected ? selected.value : null, vendorName: selected ? selected.label : "" }));
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setError(""); setSuccess(""); setLoading(true);
  try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.vendorName?.trim()) params.append("vendorName", filters.vendorName.trim());

      const res = await customFetch(`/MedPurchase/search?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPurchases(data.data || []);
      setSuccess(`Found ${data.count || 0} purchase(s) with ${data.totalItems || 0} item(s).`);
      setCurrentPage(1); setExpandedRows(new Set());
    } catch (err) { setError(err.message); setPurchases([]); } finally { setLoading(false); }
  };

  const handleReset = () => {
    setFilters({ fromDate: new Date().toISOString().split("T")[0], toDate: new Date().toISOString().split("T")[0], vendorId: null, vendorName: "" });
    setPurchases([]); setError(""); setSuccess(""); setCurrentPage(1); setExpandedRows(new Set());
  };

  const handleRowsPerPageChange = (e) => {
    let value = parseInt(e.target.value);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value); setCurrentPage(1);
  };

  const toggleExpand = (purchId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(purchId)) newSet.delete(purchId); else newSet.add(purchId);
      return newSet;
    });
  };

  const openEditModal = (groupedPurchase) => { setEditPurchase(groupedPurchase); setShowEditModal(true); };
  const closeEditModal = () => { setShowEditModal(false); setEditPurchase(null); };

  const handleDelete = async (groupedPurchase) => {
    if (!window.confirm(`Delete Purchase: ${groupedPurchase.summary?.billNo}?\n\nThis will reverse all stock entries.`)) return;
    setError(""); setSuccess("");
   try {
      const res = await customFetch(`/MedPurchase/delete/${groupedPurchase.purchID}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPurchases(prev => prev.filter(p => p.purchID !== groupedPurchase.purchID));
      setExpandedRows(prev => { const newSet = new Set(prev); newSet.delete(groupedPurchase.purchID); return newSet; });
      setSuccess(`✅ ${data.message}: ${data.billNo}`);
    } catch (err) { setError(`❌ Delete failed: ${err.message}`); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const effectiveRows = rowsPerPage || 10;
  const totalPages = Math.ceil(purchases.length / effectiveRows);
  const startIndex = (currentPage - 1) * effectiveRows;
  const currentPurchases = purchases.slice(startIndex, startIndex + effectiveRows);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    return (
      <div className="pagination-controls">
        <button className="btn-page" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => (
            <button key={num} className={`btn-page ${currentPage === num ? "active" : ""}`} onClick={() => handlePageChange(num)}>{num}</button>
        ))}
        <button className="btn-page" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
      </div>
    );
  };

  return (
    <div className="purchase-search-container">
      <div className="search-card">
        {/* Compact Header */}
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-cart-check"></i></div>
             <div className="header-text"><h5>Purchase Search</h5><p>View and manage purchases</p></div>
          </div>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="alert-custom alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* Filters - Compact Grid */}
          <div className="search-section">
            <div className="filter-grid">
              <div className="form-group">
                 <label>From Date</label>
                 <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className="form-input" />
              </div>
              <div className="form-group">
                 <label>To Date</label>
                 <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className="form-input" />
              </div>
              <div className="form-group flex-2">
                 <label>Vendor Name</label>
                 <AsyncSelect 
                    cacheOptions defaultOptions loadOptions={loadVendorOptions} onChange={handleVendorChange} 
                    value={filters.vendorId ? { value: filters.vendorId, label: filters.vendorName } : null} 
                    placeholder="Search vendor..." isClearable styles={selectStyles} menuPortalTarget={document.body} 
                 />
              </div>
              <div className="form-group" style={{maxWidth: '80px'}}>
                 <label>Rows</label>
                 <input type="number" value={rowsPerPage} onChange={handleRowsPerPageChange} className="form-input" min="1" max="100" />
              </div>
              <div className="form-group search-btn-group">
                 <label>&nbsp;</label> {/* Spacing alignment */}
                 <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="btn-search" onClick={handleSearch} disabled={loading}>
                       {loading ? <i className="bi bi-arrow-clockwise spinning"></i> : <i className="bi bi-search"></i>} Search
                    </button>
                    <button className="btn-reset" onClick={handleReset}><i className="bi bi-x-lg"></i></button>
                 </div>
              </div>
            </div>
          </div>

          <div className="records-info">
             Showing <span className="fw-bold">{purchases.length > 0 ? startIndex + 1 : 0}</span> to <span className="fw-bold">{Math.min(startIndex + effectiveRows, purchases.length)}</span> of <span className="fw-bold">{purchases.length}</span> purchases
          </div>

          {/* Table */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th style={{width: '20px'}}></th>
                    <th>Sr</th>
                    <th>ID</th>
                    <th>Bill No</th>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>GSTIN</th>
                    <th className="text-center">Items</th>
                    <th className="text-right">Taxable</th>
                    <th className="text-right">GST</th>
                    <th className="text-right">Total</th>
                    <th>Entry By</th>
                    <th className="text-center" style={{width: '70px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="13" className="empty-state"><i className="bi bi-arrow-clockwise spinning"></i> Loading data...</td></tr>
                  ) : currentPurchases.length === 0 ? (
                    <tr><td colSpan="13" className="empty-state">No purchases found.</td></tr>
                  ) : (
                    currentPurchases.map((purchase, index) => (
                      <React.Fragment key={purchase.purchID}>
                        <tr className={expandedRows.has(purchase.purchID) ? "expanded-row" : ""}>
                          <td className="text-center toggle-cell" onClick={() => toggleExpand(purchase.purchID)}>
                             <i className={`bi ${expandedRows.has(purchase.purchID) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          </td>
                          <td>{startIndex + index + 1}</td>
                          <td className="fw-medium text-primary">{purchase.purchID}</td>
                          <td className="fw-bold">{purchase.summary?.billNo || "-"}</td>
                          <td>{formatDate(purchase.summary?.billDate)}</td>
                          <td>{purchase.vendor?.vendorName || "-"}</td>
                          <td className="text-muted">{purchase.vendor?.gstin || "-"}</td>
                          <td className="text-center"><span className="badge-count">{purchase.itemCount}</span></td>
                          <td className="text-right text-muted">₹{purchase.summary?.taxable?.toFixed(2) || 0}</td>
                          <td className="text-right text-muted">₹{purchase.summary?.gstamt?.toFixed(2) || 0}</td>
                          <td className="text-right fw-bold text-success">₹{purchase.summary?.totals?.toFixed(2) || 0}</td>
                          <td><span className="user-badge"><i className="bi bi-person"></i> {purchase.summary?.entryBy || "-"}</span></td>
                          <td className="text-center">
                             <div className="btn-group-actions">
                                <button className="btn-icon edit" onClick={() => openEditModal(purchase)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                <button className="btn-icon delete" onClick={() => handleDelete(purchase)} title="Delete"><i className="bi bi-trash"></i></button>
                             </div>
                          </td>
                        </tr>

                        {/* Expandable Nested Table */}
                        {expandedRows.has(purchase.purchID) && (
                          <tr className="nested-row">
                            <td colSpan="13" className="p-0">
                               <div className="nested-table-container">
                                  <table className="nested-table">
                                     <thead>
                                        <tr>
                                           <th>Medicine</th>
                                           <th>Batch</th>
                                           <th>Qty</th>
                                           <th>MRP</th>
                                           <th>Rate</th>
                                           <th>Exp Date</th>
                                           <th className="text-right">Amount</th>
                                        </tr>
                                     </thead>
                                     <tbody>
                                        {purchase.items.map((item) => (
                                           <tr key={item.pid}>
                                              <td className="fw-medium">{item.medicine}</td>
                                              <td className="text-muted">{item.batchNo || "-"}</td>
                                              <td>{item.qty || 0}</td>
                                              <td>₹{item.mrp || 0}</td>
                                              <td>₹{item.sRate || 0}</td>
                                              <td>{formatDate(item.expDate)}</td>
                                              <td className="text-right fw-bold">₹{item.totals?.toFixed(2) || 0}</td>
                                           </tr>
                                        ))}
                                     </tbody>
                                  </table>
                               </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && <div className="pagination-section">{renderPagination()}</div>}

        </div>
      </div>

      {showEditModal && editPurchase && (
        <MedPurchaseEditModal show={showEditModal} purchaseData={editPurchase} onClose={closeEditModal} onUpdate={handleSearch} />
      )}

      {/* COMPACT STYLES */}
      <style jsx>{`
        .purchase-search-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .search-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }
        
        /* Header */
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 12px; border-bottom: 2px solid #00d4aa; }
        .header-left { display: flex; align-items: center; gap: 8px; color: white; }
        .header-icon { width: 26px; height: 26px; background: rgba(255, 255, 255, 0.2); border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .card-content { padding: 12px; }

        /* Alerts */
        .alert-custom { margin-bottom: 12px; padding: 6px 10px; border-radius: 4px; display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }

        /* Filter Grid */
        .search-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; margin-bottom: 12px; }
        .filter-grid { display: grid; grid-template-columns: 1fr 1fr 2fr auto auto; gap: 10px; align-items: end; }
        
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label { font-size: 11px; font-weight: 600; color: #4a5568; white-space: nowrap; }
        
        .form-input { padding: 0 8px; height: 30px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; outline: none; transition: 0.2s; background: white; }
        .form-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 1px var(--theme-color, #0088cc); }

        .search-btn-group { display: flex; align-items: center; gap: 6px; }
        .btn-search { padding: 0 12px; height: 30px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; font-size: 11px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: 0.2s; }
        .btn-search:hover:not(:disabled) { opacity: 0.9; }
        .btn-reset { width: 30px; height: 30px; background: #edf2f7; color: #4a5568; border: 1px solid #cbd5e0; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; font-size: 12px; }
        .btn-reset:hover { background: #e2e8f0; }

        .records-info { font-size: 11px; color: #718096; margin-bottom: 8px; text-align: right; }

        /* Main Table */
        .table-section { border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .custom-table th { background: #edf2f7; color: #4a5568; padding: 8px 10px; font-weight: 600; text-transform: uppercase; white-space: nowrap; border-bottom: 1px solid #cbd5e0; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; color: #2d3748; white-space: nowrap; }
        .custom-table tr:hover { background: #f8fafc; }
        
        .toggle-cell { cursor: pointer; color: var(--theme-color, #0088cc); font-size: 12px; font-weight: bold; width: 24px; padding: 0 !important; }
        .badge-count { background: #ebf8ff; color: #3182ce; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 10px; }
        .expanded-row { background-color: #f0f9ff !important; }
        .user-badge { display: flex; align-items: center; gap: 4px; background: #edf2f7; padding: 2px 6px; border-radius: 3px; font-size: 10px; }

        /* Nested Table */
        .nested-row td { background: #f8fafc; padding: 0; border-bottom: 1px solid #e2e8f0; }
        .nested-table-container { padding: 8px 12px 12px 30px; border-left: 3px solid var(--theme-color, #0088cc); background: #f8fafc; }
        .nested-table { width: 100%; font-size: 11px; border: 1px solid #e2e8f0; border-radius: 4px; background: white; }
        .nested-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; }
        .nested-table td { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; }

        /* Typography & Utilities */
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #2f855a; }
        .text-primary { color: var(--theme-color, #3182ce); }
        .text-muted { color: #718096; }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Actions */
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        .btn-icon { width: 22px; height: 22px; border-radius: 4px; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: 0.2s; }
        .btn-icon.edit { background: #ebf8ff; color: #3182ce; }
        .btn-icon.edit:hover { background: #bee3f8; }
        .btn-icon.delete { background: #fff5f5; color: #e53e3e; }
        .btn-icon.delete:hover { background: #fed7d7; }
        .empty-state { text-align: center; padding: 30px; color: #a0aec0; font-style: italic; }

        /* Pagination */
        .pagination-section { display: flex; justify-content: center; margin-top: 12px; }
        .pagination-controls { display: flex; gap: 4px; }
        .btn-page { width: 26px; height: 26px; border: 1px solid #e2e8f0; background: white; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #4a5568; transition: 0.2s;}
        .btn-page:hover:not(:disabled) { background: #edf2f7; }
        .btn-page.active { background: var(--theme-color, #3182ce); color: white; border-color: var(--theme-color, #3182ce); font-weight: 600; }
        .btn-page:disabled { opacity: 0.4; cursor: not-allowed; }

        /* Responsive Breakpoints */
        @media (max-width: 992px) {
          .filter-grid { grid-template-columns: 1fr 1fr; }
          .flex-2 { grid-column: span 2; }
          .search-btn-group { grid-column: span 2; }
        }
        @media (max-width: 576px) {
          .filter-grid { grid-template-columns: 1fr; }
          .flex-2 { grid-column: span 1; }
          .search-btn-group { grid-column: span 1; }
          .btn-search { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default MedicinePurchaseSearch;